import React, { FC, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { unionBy } from 'lodash'

import { SimpleEmpty } from '../../empty/SimpleEmpty/SimpleEmpty'
import { Button } from '../../../../../ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../../ui/tooltip'
import { TeamItem } from './components/TeamItem/TeamItem'

import UserService from '../../../../../../services/firebase-service/users/userService'
import { HandleModalContext } from '../../../../../../layout/mainlayout/MainLayout'

import { sortTeamMembersByRegistrar } from '../../../../../../helpers/teamMembers'
import useToasterHelper from '../../../../../../helpers/ToasterHelper'

import { useAppSelector } from '../../../../../../store/hooks'
import { selectProfileData } from '../../../../../../store/user/userSlice'

import { IUserInterface } from '../../../../../../models/users/user.interface'
import { IEventReviewPublish } from '../../../../../../models/event-review-publish/event-review-publish.interface'
import { IRiderTeamMemberInterface } from '../../../../../../models/rider-team-member/riderTeamMember.interface'

import { CONST } from '../../../../../../const/const'
import { MODAL_CONSTS } from '../../../../../../const/modal-const'

export interface IUserInterfaceTeamRegistrar extends IUserInterface {
  teamMemberRole: string
}

export type ICount = {
  classes: number
  fees: number
  tickets: number
  paperwork: number
  horses: number
}

interface TeamRegistrarTabProps {
  riderTeamMembers: IRiderTeamMemberInterface[]
  eventRequiredUserFields?: string[]
  registrar: IUserInterface | null
  eventId: string
  registrationId: string
  eventReviewPublish: IEventReviewPublish | null
  defaultOpenedPrimaryAccordion?: string
  defaultOpenedSecondaryAccordion?: 'classes' | 'fees' | 'tickets' | 'team'
}

export const TeamRegistrarTab: FC<TeamRegistrarTabProps> = ({
  riderTeamMembers,
  registrar,
  eventId,
  registrationId,
  eventReviewPublish,
  eventRequiredUserFields,
  defaultOpenedPrimaryAccordion,
  defaultOpenedSecondaryAccordion,
}) => {
  const toastFunctions = useToasterHelper()
  const history = useHistory()

  const handleModalContext = useContext(HandleModalContext)

  const profileData = useAppSelector(selectProfileData)

  const [loading, setLoading] = useState(true)
  const [riderTeamMembersWithUser, setRiderTeamMembersWithUser] = useState<
    IUserInterfaceTeamRegistrar[]
  >([])
  const [organizer, setOrganizer] = useState<IUserInterface | null>(null)
  const [users, setUsers] = useState<IUserInterface[]>([])

  const allowAdds =
    eventReviewPublish?.EventDetails?.allowAdds || profileData.id === eventReviewPublish?.owner

  const onAddEntry = () => {
    handleModalContext?.handleModal(false, MODAL_CONSTS.RIDER_INFO)
    handleModalContext?.handleModal(false, MODAL_CONSTS.REGISTRAR_INFO)
    history.push(`${CONST.ROUTES.MANAGE_REGISTER_EVENT.URL}/${eventId}/${registrar?.id}`)
  }

  useEffect(() => {
    const getData = async () => {
      if (!eventReviewPublish?.owner || !registrar) {
        toastFunctions.error({
          message: 'The registrar is having problems. Please contact support.',
        })
        return
      }

      try {
        setLoading(true)
        const teamMembersIds: Set<string> = new Set()

        riderTeamMembers?.forEach((riderTeamMember) => {
          if (riderTeamMember.teamMemberId) teamMembersIds.add(riderTeamMember.teamMemberId)
        })

        teamMembersIds.add(registrar.id)

        const organizerInDb = await UserService.getUserById(eventReviewPublish.owner)

        const usersInDb = await UserService.getByIds([...teamMembersIds])
        setUsers(usersInDb)
        setOrganizer(organizerInDb)
      } catch (error) {
        console.error(error, 'error')
      } finally {
        setLoading(false)
      }
    }
    if (eventReviewPublish?.id) getData().then()
  }, [eventReviewPublish, registrar, riderTeamMembers, toastFunctions])

  useEffect(() => {
    if (!registrar?.id) return

    let riderTeamMembersWithUser_: IUserInterfaceTeamRegistrar[] = []
    riderTeamMembers?.forEach((riderTeamMember) => {
      const user = users.find((item) => item.id === riderTeamMember.teamMemberId)
      const isExist = riderTeamMembersWithUser_.find(
        (item) => item?.id === riderTeamMember.teamMemberId
      )
      if (user && !isExist) {
        riderTeamMembersWithUser_.push({
          ...user,
          teamMemberRole: riderTeamMember.teamMemberRole || '',
        })
      }
    })

    // we need to add a registrar
    const user = users.find((item) => item.id === registrar?.id)
    if (user?.id)
      riderTeamMembersWithUser_.push({
        ...user,
        teamMemberRole: 'Registrar',
      })

    riderTeamMembersWithUser_ = sortTeamMembersByRegistrar(riderTeamMembersWithUser_, registrar?.id)

    riderTeamMembersWithUser_ = unionBy(riderTeamMembersWithUser_, 'id')

    setRiderTeamMembersWithUser(riderTeamMembersWithUser_)
  }, [registrar?.id, riderTeamMembers, users])

  if (loading) return <SimpleEmpty />

  return (
    <div className="flex flex-col gap-5">
      {riderTeamMembersWithUser?.[0] ? (
        riderTeamMembersWithUser.map((item, index) => (
          <TeamItem
            key={item.id}
            index={index}
            item={item}
            setUser={(value) => {
              const usersToUpdate = users.map((user) => (user.id === value.id ? value : user))
              setUsers(usersToUpdate)
            }}
            organizer={organizer}
            riderTeamMembers={riderTeamMembers}
            registrar={registrar}
            eventId={eventId}
            registrationId={registrationId}
            eventReviewPublish={eventReviewPublish}
            defaultOpenedPrimaryAccordion={defaultOpenedPrimaryAccordion}
            defaultOpenedSecondaryAccordion={defaultOpenedSecondaryAccordion}
            eventRequiredUserFields={eventRequiredUserFields}
          />
        ))
      ) : (
        <p className="opacity-50 text-center">No team members found</p>
      )}
      <Tooltip>
        <TooltipTrigger className="w-full">
          <Button
            disabled={!allowAdds}
            variant="secondary"
            className="w-full text-base font-normal underline hover:no-underline bg-black/5 text-black/50 p-3"
            onClick={onAddEntry}
          >
            Add Entry
          </Button>
        </TooltipTrigger>
        <TooltipContent hidden={allowAdds}>Disabled by show</TooltipContent>
      </Tooltip>
    </div>
  )
}
